import { getIsCardslistLoading } from "redux/SingleDataset/selectors";
import ImageWithFallback from "views/components/ImageWithFallback";
import ScrollAwareHover from "views/components/ScrollAwareHover";
import { CardBodyImage, ImageRelevance, OnUpdateAssetData } from "types";
import useHoverDelay from "helpers/utility/utilities";
import { useMemo } from "react";
import { connect } from "react-redux";
import classNames from "classnames";

import styles from "./style.module.scss";
import RelevanceIcon from "views/uikit/RelevanceIcon";
import { FrameIndication } from "../FrameIndication/FrameIndication";
import { isNullOrUndefined } from "../../../../helpers/utility/isNullOrUndefined";

export interface SquareImageBoxProps {
  hoverDelay?: number;
  showAssetPreview?: boolean;
  plainImages: CardBodyImage[];
  thumbImages: CardBodyImage[];
  onUpdateAssetData?: OnUpdateAssetData;
}

interface SquaredImageProps {
  hoverDelay: number;
  showAssetPreview: boolean;
  plainImage: CardBodyImage;
  thumbImage: CardBodyImage;
  relevance: ImageRelevance;
  onUpdateAssetData: () => void;
}

const GRID_ROWS_COLS_BY_DISPLAYED_IMAGES_COUNT: Record<
  number,
  [number, number]
> = {
  9: [3, 3],
  6: [2, 3],
  4: [2, 2],
  3: [3, 1],
  2: [2, 1],
  1: [1, 1],
};

const SquaredImage = ({
  plainImage,
  thumbImage,
  hoverDelay,
  relevance,
  showAssetPreview,
  onUpdateAssetData,
}: SquaredImageProps) => {
  const tableColumnClassNames = classNames(styles.tableColumn, {
    [styles.tableColumnForAssetPreview]: showAssetPreview,
  });

  const { handleMouseEnter, handleMouseLeave } = useHoverDelay(
    onUpdateAssetData,
    hoverDelay,
  );

  return (
    <ScrollAwareHover
      className={tableColumnClassNames}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <>
        {!isNullOrUndefined(plainImage.timestamp) && (
          <FrameIndication timestamp={plainImage.timestamp!} />
        )}
        {relevance && <RelevanceIcon relevance={relevance} />}
        <ImageWithFallback
          thumbImage={thumbImage}
          plainImage={plainImage}
          className={styles.singleSquareImage}
        />
      </>
    </ScrollAwareHover>
  );
};

const SquareImageBox = ({
  plainImages,
  thumbImages,
  hoverDelay = 0,
  showAssetPreview = true,
  onUpdateAssetData = () => {},
}: SquareImageBoxProps) => {
  const displayedImagesCount = useMemo(() => {
    if (!plainImages?.length) return 0;
    if (plainImages.length >= 9) return 9;
    if (plainImages.length >= 6) return 6;
    if (plainImages.length >= 4) return 4;
    return plainImages.length;
  }, [plainImages]);

  const gridStyle = () => {
    if (displayedImagesCount) {
      const [rows, columns] =
        GRID_ROWS_COLS_BY_DISPLAYED_IMAGES_COUNT[displayedImagesCount];
      return {
        gridTemplateColumns: `repeat(${columns}, minmax(1px, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(1px, 1fr))`,
      };
    }
    return {};
  };

  const mergedStyle: any = {
    ...gridStyle(),
    visibility: !!displayedImagesCount ? "visible" : "hidden",
  };

  const dynamicUpdateAssetData = (index: number) => {
    const source = plainImages[index].src;
    onUpdateAssetData(plainImages[index].id, source);
  };

  return (
    <div
      className={classNames(
        styles.squareImagesContainer,
        styles[`images_${displayedImagesCount}`],
      )}
      data-testid={"square-images-container"}
      style={mergedStyle}
    >
      {plainImages?.length &&
        plainImages.map((plainImage: CardBodyImage, i: number) => {
          const thumbImage = thumbImages?.[i] || null;

          return (
            i < displayedImagesCount && (
              <SquaredImage
                key={i}
                plainImage={plainImage}
                thumbImage={thumbImage}
                hoverDelay={hoverDelay}
                showAssetPreview={showAssetPreview}
                relevance={plainImage.relevance}
                onUpdateAssetData={() => dynamicUpdateAssetData(i)}
              />
            )
          );
        })}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return { isCardslistLoading: getIsCardslistLoading(state) };
};

export default connect(mapStateToProps)(SquareImageBox);
